import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { 
  Truck, 
  ArrowLeft,
  Wrench,
  Users,
  FileText,
  Info,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MapPin,
  Fuel,
  Edit,
  Plus,
  Eye,
  Clock,
  User,
  ClipboardCheck,
  X,
  Trash2,
  MinusCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import MaintenanceTracker from '../components/vehicles/MaintenanceTracker';
import VehicleInspection from '../components/vehicles/VehicleInspection';
import VehicleAssignment from '../components/vehicles/VehicleAssignment';
import VehicleDocuments from '../components/vehicles/VehicleDocuments';
import { useAuth } from '../contexts/AuthContext';
import vehicleService from '../services/vehicleService';
import driverService from '../services/driverService';
import inspectionService from '../services/inspectionService';

const createEmptyVehicle = (id) => ({
  id: id || null,
  vehicleNumber: '',
  make: '',
  model: '',
  year: '',
  type: '',
  status: '',
  location: '',
  mileage: null,
  fuelLevel: null,
  vin: '',
  licensePlate: '',
  purchaseDate: '',
  insuranceExpiry: '',
  registrationExpiry: '',
  lastInspection: '',
  nextInspectionDue: '',
  lastMaintenance: '',
  nextMaintenanceDue: '',
  assignedDriver: 'Unassigned',
  assignedDriverId: null,
  specifications: {}
});

const formatDate = (value, fallback = 'Not set') => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString();
};

const formatNumber = (value, fallback = '—') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return fallback;
  }

  return numeric.toLocaleString();
};

const formatInspectionType = (type) => {
  if (!type) return 'Unknown';

  const typeMap = {
    'pre-trip': 'Pre-Trip',
    'post-trip': 'Post-Trip',
    'load-inspection': 'Load Inspection',
    'annual': 'Annual',
    'DOT': 'DOT',
    'dot-annual': 'DOT Annual',
    'roadside': 'Roadside',
    'maintenance': 'Maintenance'
  };

  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

const parseTimestamp = (value) => {
  if (!value) return null;

  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const milliseconds = (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const transformInspectionRecord = (inspection = {}) => {
  const completedAt = parseTimestamp(inspection.completedAt) || parseTimestamp(inspection.completedDate);
  const scheduledDate = parseTimestamp(inspection.scheduledDate);
  const createdAt = parseTimestamp(inspection.createdAt);
  const updatedAt = parseTimestamp(inspection.updatedAt);
  const nextInspectionDue = parseTimestamp(inspection.nextInspectionDue);
  const photos = Array.isArray(inspection.photos)
    ? inspection.photos.map((photo) => ({
        ...photo,
        url: photo.url || photo.fileUrl || photo.preview || photo.thumbnailUrl || '',
        thumbnailUrl: photo.thumbnailUrl || photo.url || photo.fileUrl || photo.preview || ''
      }))
    : [];
  const defects = Array.isArray(inspection.defects) ? inspection.defects : [];
  const checklist = inspection.checklist || {};
  const location = typeof inspection.location === 'string' 
    ? { address: inspection.location }
    : (inspection.location || {});

  const type = inspection.inspectionType || inspection.type || 'Vehicle Inspection';

  console.log('Transform inspection:', {
    id: inspection.id,
    hasInspectionType: !!inspection.inspectionType,
    inspectionTypeValue: inspection.inspectionType,
    hasType: !!inspection.type,
    typeValue: inspection.type,
    calculatedType: type
  });

  const inspector = inspection.inspectorName || inspection.inspector || inspection.signedBy || inspection.inspectorId || 'Unknown';
  const status = inspection.status || (inspection.passed === true ? 'passed' : inspection.passed === false ? 'failed' : 'completed');
  const digitalSignature = inspection.digitalSignature || inspection.signature || null;

  // Spread first, then override specific fields to ensure our calculated values take precedence
  const transformed = {
    ...inspection,
    id: inspection.id || inspection.inspectionId,
    inspector,
    status,
    date: completedAt || scheduledDate || createdAt || new Date(),
    completedAt,
    scheduledDate,
    createdAt,
    updatedAt,
    nextInspectionDue,
    location,
    notes: inspection.completionNotes || inspection.notes || '',
    mileage: inspection.mileage ?? inspection.odometer ?? inspection.odometerReading ?? null,
    engineHours: inspection.engineHours ?? null,
    photos,
    checklist,
    defects,
    defectCount: defects.length,
    digitalSignature,
    passed: inspection.passed ?? (status === 'passed')
  };

  // Set type last to ensure it overrides any existing type field from the spread
  transformed.type = type;

  console.log('Final transformed type:', transformed.type);

  return transformed;
};

const humanizeLabel = (value = '') => {
  return value
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
};

const normalizeChecklistStatus = (value) => {
  const strValue = (value || '').toString().trim().toLowerCase();
  const passValues = ['pass', 'passed', 'ok', 'good', 'yes', 'true', 'satisfactory'];
  const failValues = ['fail', 'failed', 'bad', 'no', 'false', 'unsatisfactory'];
  const naValues = ['na', 'n/a', 'not applicable', 'skip'];

  if (passValues.includes(strValue)) return 'pass';
  if (failValues.includes(strValue)) return 'fail';
  if (naValues.includes(strValue)) return 'na';
  return strValue || 'unknown';
};

const normalizeChecklistEntries = (checklist = {}) => {
  const entries = [];
  
  // Check if this is a flat structure (items directly contain status/notes/timestamp)
  const isFlat = Object.values(checklist).some(value => 
    value && typeof value === 'object' && !Array.isArray(value) && 
    ('status' in value || 'notes' in value || 'timestamp' in value)
  );

  if (isFlat) {
    // Handle flat structure: { lights_functioning: { status: "fail", notes: "...", timestamp: "..." } }
    Object.entries(checklist).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const statusValue = value.status;
        const notes = value.notes ? value.notes.toString() : '';
        const timestamp = parseTimestamp(value.timestamp);
        const normalizedStatus = normalizeChecklistStatus(statusValue);
        
        entries.push({
          id: key,
          category: 'Inspection Items', // Default category for flat structure
          label: humanizeLabel(key),
          status: normalizedStatus,
          notes,
          timestamp
        });
      }
    });
  } else {
    // Handle nested structure: { category: { item_status: "fail", item_notes: "..." } }
    Object.entries(checklist).forEach(([category, items]) => {
      const groupedItems = {};

      Object.entries(items || {}).forEach(([rawKey, rawValue]) => {
        const match = rawKey.match(/(.+?)_(status|notes|timestamp)$/i);
        const key = match ? match[1] : rawKey;
        const field = match ? match[2].toLowerCase() : 'value';

        if (!groupedItems[key]) {
          groupedItems[key] = { value: undefined, status: undefined, notes: undefined, timestamp: undefined };
        }

        if (field === 'status') {
          groupedItems[key].status = rawValue;
        } else if (field === 'notes') {
          groupedItems[key].notes = (rawValue || '').toString();
        } else if (field === 'timestamp') {
          groupedItems[key].timestamp = rawValue;
        } else {
          groupedItems[key].value = rawValue;
        }
      });

      Object.entries(groupedItems).forEach(([key, data]) => {
        let statusValue = data.status;
        let notes = data.notes;
        let timestamp = parseTimestamp(data.timestamp);
        let baseValue = data.value;

        if (baseValue && typeof baseValue === 'object' && !Array.isArray(baseValue)) {
          statusValue = statusValue ?? baseValue.status ?? baseValue.value ?? baseValue.result ?? baseValue.passed;
          notes = notes ?? baseValue.notes ?? baseValue.comment ?? '';
          timestamp = timestamp || parseTimestamp(baseValue.timestamp) || parseTimestamp(baseValue.updatedAt);
        }

        if (typeof baseValue === 'boolean') {
          statusValue = statusValue ?? (baseValue ? 'pass' : 'fail');
        } else if (typeof baseValue === 'string') {
          if (!statusValue && ['pass', 'fail', 'na'].includes(baseValue.toLowerCase().trim())) {
            statusValue = baseValue;
          } else if (!notes) {
            notes = baseValue;
          }
        }

        const normalizedStatus = normalizeChecklistStatus(statusValue);

        entries.push({
          id: `${category}-${key}`,
          category: humanizeLabel(category),
          label: humanizeLabel(key),
          status: normalizedStatus,
          notes,
          timestamp
        });
      });
    });
  }

  return entries;
};

const CHECKLIST_STATUS_META = {
  pass: {
    label: 'Pass',
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200'
  },
  fail: {
    label: 'Fail',
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200'
  },
  na: {
    label: 'N/A',
    icon: MinusCircle,
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200'
  },
  'n/a': {
    label: 'N/A',
    icon: MinusCircle,
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200'
  },
  unknown: {
    label: 'Pending',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  }
};

const getChecklistStatusMeta = (status) => {
  return CHECKLIST_STATUS_META[status] || CHECKLIST_STATUS_META.unknown;
};

const VehicleProfilePage = ({
  vehicleId = 1,
  onBack,
  initialTab = null,
  allowedTabs = null,
  readOnly = false,
  allowInspectionActions = false
}) => {
  const { company, user, userData } = useAuth();
  const companyId = company?.id || company?.companyId || null;
  const userId = user?.uid || userData?.id || userData?.uid || null;
  const [activeTab, setActiveTab] = useState(initialTab || 'details');
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showInspectionTypeModal, setShowInspectionTypeModal] = useState(false);
  const [showScheduleInspectionModal, setShowScheduleInspectionModal] = useState(false);
  const [inspectionType, setInspectionType] = useState('pre-trip');
  const [inspectionHistory, setInspectionHistory] = useState([]);
  const [scheduledInspections, setScheduledInspections] = useState([]);
  const [loadingInspections, setLoadingInspections] = useState(false);
  const [showInspectionDetailModal, setShowInspectionDetailModal] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [inspectionDetailLoading, setInspectionDetailLoading] = useState(false);
  const [inspectionDetailError, setInspectionDetailError] = useState(null);
  const [inspectionDeleteLoading, setInspectionDeleteLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isChecklistCollapsed, setIsChecklistCollapsed] = useState(true);
  const [currentDriverId, setCurrentDriverId] = useState(null);
  const [vehicle, setVehicle] = useState(() => createEmptyVehicle(vehicleId));
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [vehicleError, setVehicleError] = useState(null);
  const [driversById, setDriversById] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState(null);
  const isReadOnly = Boolean(readOnly);
  const canStartInspection = !isReadOnly || allowInspectionActions;

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (!companyId) {
      setVehicle(createEmptyVehicle(vehicleId));
      setVehicleError('Company context missing. Please select a company to view vehicles.');
      return;
    }

    if (!vehicleId) {
      setVehicle(createEmptyVehicle(vehicleId));
      setVehicleError('Vehicle not specified.');
      return;
    }

    let isMounted = true;

    const fetchVehicle = async () => {
      setLoadingVehicle(true);
      setVehicleError(null);

      try {
        const fetchedVehicle = await vehicleService.getVehicle(vehicleId, { companyId });
        if (!isMounted) {
          return;
        }

        setVehicle(fetchedVehicle);
        setCurrentDriverId(fetchedVehicle.assignedDriverId || null);
      } catch (error) {
        console.error('Failed to load vehicle:', error);
        if (isMounted) {
          setVehicleError(error.getUserFriendlyMessage?.() || error.message || 'Failed to load vehicle details');
        }
      } finally {
        if (isMounted) {
          setLoadingVehicle(false);
        }
      }
    };

    fetchVehicle();

    return () => {
      isMounted = false;
    };
  }, [companyId, vehicleId]);

  const loadDrivers = useCallback(async ({ signal } = {}) => {
    if (!companyId) {
      if (signal?.aborted) {
        return;
      }

      setDrivers([]);
      setDriversById({});
      setDriversError('Company context missing. Please select a company to view drivers.');
      return;
    }

    if (signal?.aborted) {
      return;
    }

    setDriversLoading(true);
    setDriversError(null);

    try {
      const result = await driverService.listDrivers({}, { companyId });
      if (signal?.aborted) {
        return;
      }

      const driverList = Array.isArray(result?.drivers) ? result.drivers : [];
      const map = {};
      driverList.forEach((driver) => {
        if (driver?.id) {
          map[driver.id] = driver;
        }
      });

      setDrivers(driverList);
      setDriversById(map);
    } catch (error) {
      if (signal?.aborted) {
        return;
      }

      console.error('Failed to load drivers:', error);
      setDrivers([]);
      setDriversById({});
      setDriversError(error.getUserFriendlyMessage?.() || error.message || 'Failed to load drivers');
    } finally {
      if (!signal?.aborted) {
        setDriversLoading(false);
      }
    }
  }, [companyId]);

  useEffect(() => {
    const controller = { aborted: false };
    loadDrivers({ signal: controller });

    return () => {
      controller.aborted = true;
    };
  }, [loadDrivers]);

  // Fetch inspection history for the vehicle
  const fetchInspectionHistory = useCallback(async () => {
    if (!companyId || !vehicleId) {
      setInspectionHistory([]);
      return;
    }

    setLoadingInspections(true);
    try {
      // Get inspection history for this vehicle
      const inspections = await inspectionService.getVehicleInspections(vehicleId);

      console.log('Raw inspections from API:', inspections);

      // Transform API data to match our UI format
      const transformedInspections = inspections.map(transformInspectionRecord);

      console.log('Transformed inspections:', transformedInspections);

      setInspectionHistory(transformedInspections);
    } catch (error) {
      console.error('Failed to fetch inspection history:', error);
      // Keep existing mock data on error
    } finally {
      setLoadingInspections(false);
    }
  }, [companyId, vehicleId]);

  // Load inspection history when component mounts or dependencies change
  useEffect(() => {
    fetchInspectionHistory();
  }, [fetchInspectionHistory]);

  // Scheduled inspections, driver assignments, and documents now load from API responses


  // Inspection management functions
  const handleStartInspection = () => {
    if (!canStartInspection) return;
    setShowInspectionTypeModal(true);
  };

  const handleInspectionTypeSelect = (type) => {
    if (!canStartInspection) return;
    setInspectionType(type);
    setShowInspectionTypeModal(false);
    setShowInspectionModal(true);
  };

  const handleScheduleInspection = () => {
    if (isReadOnly) return;
    setShowScheduleInspectionModal(true);
  };

  const handleScheduleInspectionSubmit = (scheduleData) => {
    if (isReadOnly) return;
    const newScheduledInspection = {
      id: scheduledInspections.length + 1,
      scheduledDate: scheduleData.date,
      scheduledTime: scheduleData.time,
      type: scheduleData.type,
      inspector: scheduleData.inspector || 'TBD',
      location: scheduleData.location || vehicle.location,
      notes: scheduleData.notes || '',
      status: 'scheduled',
      priority: scheduleData.priority || 'medium',
      estimatedDuration: scheduleData.estimatedDuration || 60,
      createdBy: 'Fleet Manager', // In real app, get from auth context
      createdDate: new Date().toISOString().split('T')[0]
    };

    setScheduledInspections([...scheduledInspections, newScheduledInspection]);
    setShowScheduleInspectionModal(false);
  };

  const handleCancelScheduledInspection = (inspectionId) => {
    if (isReadOnly) return;
    setScheduledInspections(scheduledInspections.filter(inspection => inspection.id !== inspectionId));
  };

  // Assignment management functions
  const handleAssignmentChange = (assignmentData = {}) => {
    const { driverId, vehicle: updatedVehicle } = assignmentData;

    if (updatedVehicle) {
      setVehicle(updatedVehicle);
      setCurrentDriverId(updatedVehicle.assignedDriverId || null);

      if (updatedVehicle.assignedDriver && updatedVehicle.assignedDriver.id) {
        setDriversById((prev) => ({
          ...prev,
          [updatedVehicle.assignedDriver.id]: {
            ...(prev[updatedVehicle.assignedDriver.id] || {}),
            ...updatedVehicle.assignedDriver
          }
        }));
      }
    } else if (driverId !== undefined) {
      setCurrentDriverId(driverId);
    }
  };

  const handleInspectionComplete = (inspectionData) => {
    // Close modal first
    setShowInspectionModal(false);
    
    // Refresh inspection history to get latest data
    fetchInspectionHistory();
  };

  // Handle viewing inspection details
  const handleViewInspectionDetails = async (inspection) => {
    if (!inspection) return;

    setSelectedInspection(inspection);
    setInspectionDetailError(null);
    setShowInspectionDetailModal(true);

    if (!inspection.id) {
      return;
    }

    try {
      setInspectionDetailLoading(true);
      const detailedInspection = await inspectionService.getInspection(inspection.id);
      setSelectedInspection(transformInspectionRecord(detailedInspection));
    } catch (error) {
      console.error('Failed to load inspection details:', error);
      setInspectionDetailError(error.getUserFriendlyMessage?.() || 'Failed to load inspection details.');
    } finally {
      setInspectionDetailLoading(false);
    }
  };

  const requestDeleteInspection = (inspection) => {
    if (!inspection?.id) return;
    setPendingDelete(inspection);
  };

  const cancelDeleteInspection = () => {
    setPendingDelete(null);
  };

  const confirmDeleteInspection = async () => {
    if (!pendingDelete?.id) return;

    try {
      setInspectionDetailError(null);
      setInspectionDeleteLoading(true);
      await inspectionService.deleteInspection(pendingDelete.id);
      setInspectionHistory(prev => prev.filter(item => item.id !== pendingDelete.id));
      setPendingDelete(null);
      setSelectedInspection(null);
      setShowInspectionDetailModal(false);
    } catch (error) {
      console.error('Failed to delete inspection:', error);
      setInspectionDetailError(error.getUserFriendlyMessage?.() || error.message || 'Failed to delete inspection.');
      setInspectionDeleteLoading(false);
    }
  };

  const handleCloseInspectionDetails = () => {
    setShowInspectionDetailModal(false);
    setInspectionDetailError(null);
    setInspectionDetailLoading(false);
  };

  const tabs = useMemo(() => ([
    { id: 'details', label: 'Vehicle Details', icon: Info },
    { id: 'inspections', label: 'Inspection History', icon: CheckCircle },
    { id: 'maintenance', label: 'Maintenance Tracker', icon: Wrench },
    { id: 'assignments', label: 'Driver Assignments', icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText }
  ]), []);

  const visibleTabs = useMemo(() => {
    if (!Array.isArray(allowedTabs) || !allowedTabs.length) {
      return tabs;
    }
    return tabs.filter((tab) => allowedTabs.includes(tab.id));
  }, [allowedTabs, tabs]);

  useEffect(() => {
    if (!visibleTabs.length) {
      return;
    }
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);


  // Inspection Type Selection Modal
  const InspectionTypeModal = ({ isOpen, onClose, onTypeSelect, vehicle }) => {
    if (!isOpen) return null;

    const inspectionTypes = [
      {
        type: 'pre-trip',
        title: 'Pre-Trip Inspection',
        description: 'Conducted before operating the vehicle to ensure it\'s safe for the trip',
        icon: '🌅',
        color: 'bg-green-50 border-green-200 hover:bg-green-100'
      },
      {
        type: 'post-trip',
        title: 'Post-Trip Inspection',
        description: 'Conducted after completing a trip to check for any issues or damage',
        icon: '🌆',
        color: 'bg-blue-50 border-blue-200 hover:bg-blue-100'
      },
      {
        type: 'load-inspection',
        title: 'Load Inspection',
        description: 'Document vehicle and trailer with photos (front with plate, back, both sides, trailer)',
        icon: '📦',
        color: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
      },
      {
        type: 'dot-annual',
        title: 'DOT Annual Inspection',
        description: 'Required annual inspection for DOT compliance',
        icon: '📋',
        color: 'bg-purple-50 border-purple-200 hover:bg-purple-100'
      },
      {
        type: 'maintenance',
        title: 'Maintenance Inspection',
        description: 'Inspection following maintenance or repair work',
        icon: '🔧',
        color: 'bg-orange-50 border-orange-200 hover:bg-orange-100'
      }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardCheck className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Start Vehicle Inspection</h3>
                <p className="text-sm text-gray-600">
                  {vehicle.vehicleNumber} - {vehicle.year} {vehicle.make} {vehicle.model}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            <div className="mb-4">
              <h4 className="text-base font-medium text-gray-900 mb-2">What type of inspection is this?</h4>
              <p className="text-sm text-gray-600">Select the type of inspection you want to perform:</p>
            </div>

            <div className="space-y-3">
              {inspectionTypes.map((inspectionType) => (
                <button
                  key={inspectionType.type}
                  onClick={() => onTypeSelect(inspectionType.type)}
                  className={`w-full text-left p-4 border-2 rounded-lg transition-colors ${inspectionType.color}`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{inspectionType.icon}</span>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{inspectionType.title}</h5>
                      <p className="text-sm text-gray-600 mt-1">{inspectionType.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-6 mt-6 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Schedule Inspection Modal Component
  const ScheduleInspectionModal = ({ isOpen, onClose, onSubmit, vehicle }) => {
    const [formData, setFormData] = useState({
      date: '',
      time: '',
      type: 'DOT Annual',
      inspector: '',
      location: vehicle?.location || '',
      notes: '',
      priority: 'medium',
      estimatedDuration: 60
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!formData.date || !formData.time) return;

      onSubmit(formData);
      setFormData({
        date: '',
        time: '',
        type: 'DOT Annual',
        inspector: '',
        location: vehicle?.location || '',
        notes: '',
        priority: 'medium',
        estimatedDuration: 60
      });
    };

    if (!isOpen) return null;

    const inspectionTypes = [
      { value: 'DOT Annual', label: 'DOT Annual Inspection' },
      { value: 'Pre-Trip', label: 'Pre-Trip Inspection' },
      { value: 'Post-Trip', label: 'Post-Trip Inspection' },
      { value: 'Maintenance', label: 'Maintenance Inspection' },
      { value: 'Safety', label: 'Safety Inspection' },
      { value: 'Custom', label: 'Custom Inspection' }
    ];

    const priorityOptions = [
      { value: 'low', label: 'Low', color: 'text-green-600' },
      { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
      { value: 'high', label: 'High', color: 'text-red-600' }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Schedule Inspection</h3>
                <p className="text-sm text-gray-600">
                  {vehicle?.vehicleNumber} - {vehicle?.year} {vehicle?.make} {vehicle?.model}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scheduled Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scheduled Time *
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* Inspection Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inspection Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {inspectionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Inspector and Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inspector (Optional)
                </label>
                <input
                  type="text"
                  value={formData.inspector}
                  onChange={(e) => setFormData({ ...formData, inspector: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter inspector name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter inspection location"
                />
              </div>
            </div>

            {/* Priority and Estimated Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {priorityOptions.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Duration (minutes)
                </label>
                <input
                  type="number"
                  value={formData.estimatedDuration}
                  onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 60 })}
                  min="15"
                  max="480"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add any additional notes or requirements..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Schedule Inspection
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Inspection Modal Component
  const InspectionModal = ({ isOpen, onClose, onComplete, vehicle, inspectionType }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto mx-4">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardCheck className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {inspectionType === 'pre-trip' ? 'Pre-Trip' :
                   inspectionType === 'post-trip' ? 'Post-Trip' :
                   inspectionType === 'load-inspection' ? 'Load' :
                   inspectionType === 'dot-annual' ? 'DOT Annual' :
                   inspectionType === 'maintenance' ? 'Maintenance' : ''} Inspection
                </h3>
                <p className="text-sm text-gray-600">
                  {vehicle.vehicleNumber} - {vehicle.year} {vehicle.make} {vehicle.model}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            <VehicleInspection
              vehicleId={vehicle.id} // Use the actual vehicle ID, not vehicle number
              driverId={null} // Driver should be selected separately, not assumed to be current user
              inspectionType={inspectionType}
              onInspectionComplete={onComplete}
            />
          </div>
        </div>
      </div>
    );
  };

  // Inspection Detail Modal Component
  const InspectionDetailModal = ({ isOpen, onClose, inspection, isLoading = false, error = null, onDeleteInspection }) => {
    const [previewPhoto, setPreviewPhoto] = useState(null);

    if (!isOpen || !inspection) return null;

    const getStatusIcon = (status) => {
      const statusDisplay = getStatusDisplay(status);
      return React.createElement(statusDisplay.icon, { 
        className: `h-5 w-5 ${statusDisplay.color}` 
      });
    };

    const formatLocationDisplay = (location) => {
      if (!location) return 'Not specified';
      if (typeof location === 'string') return location;
      const parts = [location.address, location.city, location.state, location.zipCode]
        .filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'Not specified';
    };

    const getSeverityStyles = (severity = 'minor') => {
      const normalized = typeof severity === 'string' ? severity.toLowerCase() : 'minor';
      const severityMap = {
        critical: { label: 'Critical', bg: 'bg-red-100', text: 'text-red-800' },
        major: { label: 'Major', bg: 'bg-orange-100', text: 'text-orange-800' },
        minor: { label: 'Minor', bg: 'bg-yellow-100', text: 'text-yellow-800' }
      };
      return severityMap[normalized] || severityMap.minor;
    };

    const locationDisplay = formatLocationDisplay(inspection.location);
    const defects = Array.isArray(inspection.defects) ? inspection.defects : [];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardCheck className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{inspection.type}</h3>
                <p className="text-sm text-gray-600">
                  {vehicle.vehicleNumber} - {formatDate(inspection.date)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {isLoading && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-sm text-blue-700">
                Loading latest inspection details...
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Inspection Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  {getStatusIcon(inspection.status)}
                  <span className="text-sm font-medium text-gray-900">Status</span>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  getStatusDisplay(inspection.status).bg
                } ${
                  getStatusDisplay(inspection.status).color
                }`}>
                  {getStatusDisplay(inspection.status).label}
                </span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <User className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">Inspector</span>
                </div>
                <p className="text-sm text-gray-600">{inspection.inspector}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">Defects</span>
                </div>
                <p className="text-sm text-gray-600">{inspection.defectCount || defects.length} reported</p>
              </div>
            </div>

            {/* Inspection Checklist */}
            {inspection.checklist && Object.keys(inspection.checklist).length > 0 && (() => {
              const checklistEntries = normalizeChecklistEntries(inspection.checklist);
              if (checklistEntries.length === 0) {
                return null;
              }

              const groupedEntries = checklistEntries.reduce((acc, entry) => {
                if (!acc[entry.category]) acc[entry.category] = [];
                acc[entry.category].push(entry);
                return acc;
              }, {});

              const statusStats = checklistEntries.reduce(
                (acc, entry) => {
                  const key = normalizeChecklistStatus(entry.status);
                  acc[key] = (acc[key] || 0) + 1;
                  acc.total += 1;
                  return acc;
                },
                { total: 0 }
              );

              const statusOrder = ['pass', 'fail', 'na', 'unknown'];

              return (
                <div className="bg-white border rounded-lg">
                  <div 
                    className="px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition-all duration-200 select-none"
                    onClick={() => setIsChecklistCollapsed(!isChecklistCollapsed)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h4 className="text-base font-semibold text-gray-900">Inspection Checklist</h4>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {statusStats.total} item{statusStats.total !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <button 
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors rounded-md px-2 py-1 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsChecklistCollapsed(!isChecklistCollapsed);
                        }}
                      >
                        <span className="font-medium">
                          {isChecklistCollapsed ? 'Show Details' : 'Hide Details'}
                        </span>
                        {isChecklistCollapsed ? (
                          <ChevronRight className="h-4 w-4 transition-transform duration-200" />
                        ) : (
                          <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="p-4 space-y-5">
                    {/* Status counts - always visible */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {statusOrder.map((key) => {
                        if (!statusStats[key]) return null;
                        const meta = getChecklistStatusMeta(key);
                        return (
                          <div key={key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${meta.bg} ${meta.border}`}>
                            {React.createElement(meta.icon, { className: `h-4 w-4 ${meta.color}` })}
                            <div>
                              <p className="text-xs text-gray-500">{meta.label}</p>
                              <p className="text-sm font-semibold text-gray-900">{statusStats[key]} item{statusStats[key] !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Collapsible detailed items */}
                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isChecklistCollapsed ? 'max-h-0 opacity-0' : 'max-h-none opacity-100'
                    }`}>
                      <div className="space-y-4 pt-1">
                        {Object.entries(groupedEntries).map(([category, items]) => (
                          <div key={category} className="border rounded-lg">
                            <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50">
                              <h5 className="text-sm font-medium text-gray-900">{category}</h5>
                              <span className="text-xs text-gray-500">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="divide-y">
                              {items.map((item) => {
                                const meta = getChecklistStatusMeta(item.status);
                                return (
                                  <div key={item.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 gap-3">
                                    <div className="space-y-2">
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                                        <p className="text-xs text-gray-500">{item.category}</p>
                                      </div>
                                      <div className="space-y-1 text-xs text-gray-600">
                                        <p>
                                          <span className="font-semibold text-gray-700">Status:</span> {meta.label}
                                        </p>
                                        {item.notes && (
                                          <p className="whitespace-pre-wrap">
                                            <span className="font-semibold text-gray-700">Notes:</span> {item.notes}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${meta.bg} ${meta.border}`}>
                                      {React.createElement(meta.icon, { className: `h-4 w-4 ${meta.color}` })}
                                      <span className={meta.color}>{meta.label}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Defects */}
            {defects.length > 0 && (
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b">
                  <h4 className="text-base font-semibold text-gray-900">Reported Defects ({defects.length})</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {defects.map((defect, index) => {
                    const severityStyles = getSeverityStyles(defect.severity);
                    const detectedAt = parseTimestamp(defect.detectedAt) || parseTimestamp(defect.reportedAt) || defect.detectedAt || defect.reportedAt;
                    const fixedAt = parseTimestamp(defect.fixedDate) || parseTimestamp(defect.repairedDate) || defect.fixedDate || defect.repairedDate;
                    const updatedAt = parseTimestamp(defect.updatedAt) || defect.updatedAt;
                    
                    return (
                      <div key={defect.id || index} className="px-4 py-4">
                        <div className="grid gap-4 lg:grid-cols-3">
                          {/* Main Info */}
                          <div className="lg:col-span-2 space-y-3">
                            <div>
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    {defect.title || defect.description || defect.category || `Defect ${index + 1}`}
                                  </h5>
                                  {defect.category && defect.category !== defect.title && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      <span className="font-medium">Category:</span> {defect.category}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${severityStyles.bg} ${severityStyles.text}`}>
                                    {severityStyles.label}
                                  </span>
                                  {defect.priority && defect.priority !== 'medium' && (
                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                      defect.priority === 'critical' || defect.priority === 'high' 
                                        ? 'bg-red-50 text-red-700 border border-red-200' 
                                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                                    }`}>
                                      {defect.priority}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {defect.description && defect.description !== defect.title && (
                                <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap">
                                  {defect.description}
                                </p>
                              )}
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-600">
                                {defect.component && defect.component !== defect.category && (
                                  <div>
                                    <span className="font-medium text-gray-700">Component:</span> {defect.component}
                                  </div>
                                )}
                                {defect.estimatedCost && Number(defect.estimatedCost) > 0 && (
                                  <div>
                                    <span className="font-medium text-gray-700">Est. Cost:</span> ${Number(defect.estimatedCost).toFixed(2)}
                                  </div>
                                )}
                                {defect.actualCost && Number(defect.actualCost) > 0 && (
                                  <div>
                                    <span className="font-medium text-gray-700">Actual Cost:</span> ${Number(defect.actualCost).toFixed(2)}
                                  </div>
                                )}
                                {defect.workOrder && (
                                  <div>
                                    <span className="font-medium text-gray-700">Work Order:</span> {defect.workOrder}
                                  </div>
                                )}
                                {defect.reportedBy && (
                                  <div>
                                    <span className="font-medium text-gray-700">Reported By:</span> {defect.reportedBy}
                                  </div>
                                )}
                              </div>

                              {(defect.notes || defect.repairNotes) && (
                                <div className="mt-3 space-y-2">
                                  {defect.notes && (
                                    <div className="bg-gray-50 rounded-md p-3">
                                      <p className="text-xs font-medium text-gray-700 mb-1">Notes:</p>
                                      <p className="text-xs text-gray-600 whitespace-pre-wrap">{defect.notes}</p>
                                    </div>
                                  )}
                                  {defect.repairNotes && defect.repairNotes !== defect.notes && (
                                    <div className="bg-blue-50 rounded-md p-3">
                                      <p className="text-xs font-medium text-gray-700 mb-1">Repair Notes:</p>
                                      <p className="text-xs text-gray-600 whitespace-pre-wrap">{defect.repairNotes}</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Defect Photos */}
                              {defect.photos && defect.photos.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-gray-700 mb-2">Related Photos ({defect.photos.length}):</p>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {defect.photos.slice(0, 4).map((photo, photoIndex) => (
                                      <div key={photo.id || photoIndex} className="aspect-square">
                                        <img
                                          src={photo.thumbnailUrl || photo.url}
                                          alt={photo.notes || `Defect photo ${photoIndex + 1}`}
                                          className="w-full h-full object-cover rounded border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                                          onClick={() => {
                                            // Could open photo in modal/lightbox
                                            window.open(photo.url, '_blank');
                                          }}
                                        />
                                      </div>
                                    ))}
                                    {defect.photos.length > 4 && (
                                      <div className="aspect-square bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                        <span className="text-xs text-gray-500">+{defect.photos.length - 4} more</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Status & Timeline */}
                          <div className="space-y-3">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <h6 className="text-xs font-medium text-gray-700 mb-2">Status & Timeline</h6>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-600">Status:</span>
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    defect.status === 'resolved' || defect.status === 'fixed' 
                                      ? 'bg-green-100 text-green-800' 
                                      : defect.status === 'in-progress' 
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {defect.status || 'reported'}
                                  </span>
                                </div>
                                
                                {detectedAt && (
                                  <div className="text-xs text-gray-600">
                                    <span className="font-medium">Reported:</span>
                                    <br />
                                    {formatDate(detectedAt)}
                                  </div>
                                )}
                                
                                {updatedAt && updatedAt !== detectedAt && (
                                  <div className="text-xs text-gray-600">
                                    <span className="font-medium">Updated:</span>
                                    <br />
                                    {formatDate(updatedAt)}
                                  </div>
                                )}
                                
                                {fixedAt && (
                                  <div className="text-xs text-green-700">
                                    <span className="font-medium">Fixed:</span>
                                    <br />
                                    {formatDate(fixedAt)}
                                    {defect.repairedBy && (
                                      <div className="mt-1">by {defect.repairedBy}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Photos */}
            {inspection.photos && inspection.photos.length > 0 && (
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b">
                  <h4 className="text-base font-semibold text-gray-900">Photos ({inspection.photos.length})</h4>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {inspection.photos.map((photo, index) => (
                      <div key={photo.id || index} className="relative group">
                        <img
                          src={photo.url || photo.thumbnailUrl}
                          alt={photo.description || `Inspection photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        <div className="absolute inset-0 rounded-lg flex items-center justify-center bg-black bg-opacity-0 transition-opacity group-hover:bg-opacity-40">
                          <button
                            type="button"
                            onClick={() => setPreviewPhoto(photo)}
                            className="hidden sm:inline-flex items-center justify-center p-2 text-white bg-black bg-opacity-60 rounded-full opacity-0 group-hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            aria-label="View photo"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPreviewPhoto(photo)}
                          className="sm:hidden absolute top-2 right-2 inline-flex items-center justify-center p-2 bg-white/90 text-gray-700 rounded-full shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
                          aria-label="View photo"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {photo.category && (
                          <div className="absolute bottom-1 left-1 bg-black bg-opacity-75 text-white text-xs px-1 py-0.5 rounded">
                            {photo.category}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {inspection.notes && (
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b">
                  <h4 className="text-base font-semibold text-gray-900">Notes</h4>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{inspection.notes}</p>
                </div>
              </div>
            )}

            {/* Digital Signature */}
            {inspection.digitalSignature && (
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b">
                  <h4 className="text-base font-semibold text-gray-900">Digital Signature</h4>
                </div>
                <div className="p-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img
                      src={inspection.digitalSignature}
                      alt="Digital signature"
                      className="max-w-full h-auto max-h-32"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Signed by {inspection.inspector} on {formatDate(inspection.date)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {inspection.createdAt && (
                  <div>
                    <span className="font-medium text-gray-500">Created:</span>
                    <p className="text-gray-900">{new Date(inspection.createdAt).toLocaleString()}</p>
                  </div>
                )}
                {inspection.updatedAt && (
                  <div>
                    <span className="font-medium text-gray-500">Last Updated:</span>
                    <p className="text-gray-900">{new Date(inspection.updatedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 p-6 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={() => onDeleteInspection?.(inspection)}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Delete Inspection
            </button>
          </div>
          {previewPhoto && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
              onClick={() => setPreviewPhoto(null)}
            >
              <div
                className="bg-white rounded-lg max-w-3xl w-full shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{previewPhoto.category || 'Inspection Photo'}</p>
                    {previewPhoto.description && (
                      <p className="text-xs text-gray-500">{previewPhoto.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewPhoto(null)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close photo preview"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4">
                  <img
                    src={previewPhoto.url || previewPhoto.thumbnailUrl}
                    alt={previewPhoto.description || 'Inspection photo preview'}
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'active': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Active' },
      'maintenance': { icon: Wrench, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Maintenance' },
      'out-of-service': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Out of Service' },
      'passed': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Passed' },
      'warning': { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Warning' },
      'failed': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' },
      'completed': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Completed' },
      'scheduled': { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Scheduled' }
    };
    return statusMap[status] || statusMap['active'];
  };

  // Vehicle Details Tab
  const VehicleDetailsTab = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Basic Information</h3>
            <button className="inline-flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-800 self-start">
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Vehicle Number</label>
              <p className="text-sm sm:text-base text-gray-900 font-medium">{vehicle.vehicleNumber}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Make & Model</label>
              <p className="text-sm sm:text-base text-gray-900">{vehicle.year} {vehicle.make} {vehicle.model}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">VIN</label>
              <p className="text-xs sm:text-sm text-gray-900 font-mono break-all">{vehicle.vin}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">License Plate</label>
              <p className="text-sm sm:text-base text-gray-900">{vehicle.licensePlate}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Purchase Date</label>
              <p className="text-sm sm:text-base text-gray-900">{formatDate(vehicle.purchaseDate)}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Current Mileage</label>
              <p className="text-sm sm:text-base text-gray-900 font-medium">
                {vehicle.mileage !== null && vehicle.mileage !== undefined
                  ? `${formatNumber(vehicle.mileage)} miles`
                  : 'Not recorded'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Status */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Current Status</h3>
        </div>
        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-900">Location</p>
                <p className="text-sm sm:text-base text-gray-600 truncate">{vehicle.location}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                <Fuel className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-900">Fuel Level</p>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${fuelLevelValue !== null ? fuelLevelValue : 0}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-600">
                    {fuelLevelValue !== null ? `${fuelLevelValue}%` : 'Not tracked'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-900">Current Driver</p>
                <p className="text-sm sm:text-base text-gray-600 truncate">{assignedDriverName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                {React.createElement(getStatusDisplay(vehicle.status).icon, { className: `h-4 w-4 sm:h-5 sm:w-5 ${getStatusDisplay(vehicle.status).color}` })}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-900">Status</p>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusDisplay(vehicle.status).bg} ${getStatusDisplay(vehicle.status).color}`}>
                  {getStatusDisplay(vehicle.status).label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Specifications */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Specifications</h3>
        </div>
        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Object.entries(vehicle.specifications || {}).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-500 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <p className="text-sm sm:text-base text-gray-900">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Important Dates */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Important Dates</h3>
        </div>
        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Insurance Expiry</label>
              <p className="text-sm sm:text-base text-gray-900">{formatDate(vehicle.insuranceExpiry)}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Registration Expiry</label>
              <p className="text-sm sm:text-base text-gray-900">{formatDate(vehicle.registrationExpiry)}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Next Inspection</label>
              <p className="text-sm sm:text-base text-gray-900">{formatDate(vehicle.nextInspectionDue)}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-500">Next Maintenance</label>
              <p className="text-sm sm:text-base text-gray-900">{formatDate(vehicle.nextMaintenanceDue)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Inspection History Tab
  const InspectionHistoryTab = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Inspection History</h3>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          {canStartInspection && (
            <button 
              onClick={handleStartInspection}
              className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Start Inspection
            </button>
          )}
          {!isReadOnly && (
            <button 
              onClick={handleScheduleInspection}
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Schedule Inspection
            </button>
          )}
        </div>
      </div>
      
      {/* Scheduled Inspections Section */}
      {scheduledInspections.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <h4 className="text-base font-semibold text-blue-900">Scheduled Inspections</h4>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                {scheduledInspections.length}
              </span>
            </div>
          </div>

          {/* Mobile Cards View for Scheduled */}
          <div className="block sm:hidden space-y-3">
            {scheduledInspections.map((inspection) => (
              <div key={inspection.id} className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900">{formatInspectionType(inspection.type)}</h5>
                    <p className="text-xs text-gray-500">
                      {formatDate(inspection.scheduledDate)} at {inspection.scheduledTime}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      inspection.priority === 'high' ? 'bg-red-100 text-red-800' :
                      inspection.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {inspection.priority}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Inspector:</span>
                    <span className="text-gray-900 text-right">{inspection.inspector}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Location:</span>
                    <span className="text-gray-900 text-right">{inspection.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Duration:</span>
                    <span className="text-gray-900">{inspection.estimatedDuration} min</span>
                  </div>
                </div>
                
                {!isReadOnly && (
                  <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-gray-100">
                    <button className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                      Edit
                    </button>
                    <button 
                      onClick={() => handleCancelScheduledInspection(inspection.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Table View for Scheduled */}
          <div className="hidden sm:block bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled Date</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inspector</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {scheduledInspections.map((inspection) => (
                    <tr key={inspection.id} className="hover:bg-gray-50">
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(inspection.scheduledDate)}
                        <div className="text-xs text-gray-500">{inspection.scheduledTime}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {inspection.type}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {inspection.inspector}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {inspection.location}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          inspection.priority === 'high' ? 'bg-red-100 text-red-800' :
                          inspection.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {inspection.priority}
                        </span>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!isReadOnly && (
                          <>
                            <button className="text-blue-600 hover:text-blue-900 mr-3" title="Edit Scheduled Inspection">
                              <Edit className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleCancelScheduledInspection(inspection.id)}
                              className="text-red-600 hover:text-red-900" 
                              title="Cancel Scheduled Inspection"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile Cards View */}
      <div className="block sm:hidden space-y-4">
        {inspectionHistory.map((inspection) => {
          const statusDisplay = getStatusDisplay(inspection.status);
          return (
            <div key={inspection.id} className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{formatInspectionType(inspection.type)}</h4>
                  <p className="text-xs text-gray-500">{formatDate(inspection.date)}</p>
                </div>
                <div className="flex items-center">
                  {React.createElement(statusDisplay.icon, { className: `h-4 w-4 mr-1 ${statusDisplay.color}` })}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                    {statusDisplay.label}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Inspector:</span>
                  <span className="text-gray-900 text-right">{inspection.inspector}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Defects:</span>
                  <span className="text-gray-900">{inspection.defectCount ?? (inspection.defects?.length || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Location:</span>
                  <span className="text-gray-900 text-right">
                    {typeof inspection.location === 'string'
                      ? inspection.location
                      : inspection.location?.address || 'Not specified'}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-gray-100">
                <button 
                  onClick={() => handleViewInspectionDetails(inspection)}
                  className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                >
                  View Details
                </button>
                {!isReadOnly && (
                  <button
                    onClick={() => requestDeleteInspection(inspection)}
                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inspector</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Defects</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inspectionHistory.map((inspection) => {
                const statusDisplay = getStatusDisplay(inspection.status);
                return (
                  <tr key={inspection.id} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(inspection.date)}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatInspectionType(inspection.type)}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {inspection.inspector}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {React.createElement(statusDisplay.icon, { className: `h-4 w-4 mr-2 ${statusDisplay.color}` })}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                          {statusDisplay.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {inspection.defectCount ?? (inspection.defects?.length || 0)}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleViewInspectionDetails(inspection)}
                        className="text-blue-600 hover:text-blue-900 mr-3" 
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {!isReadOnly && (
                        <button
                          onClick={() => requestDeleteInspection(inspection)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Inspection"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Maintenance Tracker Tab - Using the new MaintenanceTracker component
  const MaintenanceTrackerTab = () => (
    <MaintenanceTracker
      vehicleId={vehicle.id || vehicleId}
      companyId={companyId}
      userId={userId}
      readOnly={isReadOnly}
    />
  );

  // Driver Assignments Tab
  const DriverAssignmentsTab = () => (
    <VehicleAssignment
      vehicleId={vehicle.id}
      vehicleNumber={vehicle.vehicleNumber}
      currentDriverId={currentDriverId}
      companyId={companyId}
      drivers={drivers}
      driversLoading={driversLoading}
      driversError={driversError}
      onAssignmentChange={handleAssignmentChange}
      onRefreshDrivers={loadDrivers}
    />
  );

  // Documents Tab - Using the new VehicleDocuments component
  const DocumentsTab = () => (
    <VehicleDocuments
      vehicleId={vehicle.id || vehicleId}
      vehicleNumber={vehicle.vehicleNumber}
      vehicle={vehicle}
    />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return <VehicleDetailsTab />;
      case 'inspections':
        return <InspectionHistoryTab />;
      case 'maintenance':
        return <MaintenanceTrackerTab />;
      case 'assignments':
        return <DriverAssignmentsTab />;
      case 'documents':
        return <DocumentsTab />;
      default:
        return <VehicleDetailsTab />;
    }
  };

  const assignedDriver = currentDriverId ? driversById[currentDriverId] : null;
  const assignedDriverName = assignedDriver?.name || vehicle.assignedDriver || 'Unassigned';
  const fuelLevelValue = typeof vehicle.fuelLevel === 'number' && Number.isFinite(vehicle.fuelLevel)
    ? Math.min(100, Math.max(0, vehicle.fuelLevel))
    : null;

  if (loadingVehicle) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border rounded-lg shadow-sm p-8 text-center text-gray-600">
          Loading vehicle details...
        </div>
      </div>
    );
  }

  if (vehicleError) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg shadow-sm p-8 text-center">
          <h2 className="text-lg font-semibold text-red-800">Unable to load vehicle</h2>
          <p className="mt-2 text-sm text-red-700">{vehicleError}</p>
          {onBack && (
            <button
              onClick={onBack}
              className="mt-6 btn-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Vehicles
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="space-y-4 sm:space-y-0">
        {/* Back Button - Full Width on Mobile */}
        {onBack && (
          <div className="sm:hidden">
            <button
              onClick={onBack}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 w-full justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Vehicles
            </button>
          </div>
        )}

        {/* Main Header Content */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Back Button - Desktop */}
            {onBack && (
              <button
                onClick={onBack}
                className="hidden sm:inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            )}
            
            {/* Vehicle Info */}
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{vehicle.vehicleNumber}</h1>
                <p className="text-sm sm:text-base text-gray-600">{vehicle.year} {vehicle.make} {vehicle.model}</p>
              </div>
            </div>
          </div>
          
          {/* Right Section - Stack on Mobile */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            {/* Action Button */}
            {canStartInspection && (
              <div className="relative">
                <button 
                  onClick={handleStartInspection}
                  className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm sm:text-base"
                >
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                  Start Inspection
                </button>
              </div>
            )}
            
            {/* Status Info - Horizontal Layout on Mobile */}
            <div className="flex items-center justify-between sm:justify-start sm:space-x-3">
              <div className="text-left sm:text-right">
                <p className="text-xs sm:text-sm text-gray-500">Location</p>
                <p className="text-sm font-medium text-gray-900">{vehicle.location}</p>
              </div>
              
              <div className="flex items-center">
                {React.createElement(getStatusDisplay(vehicle.status).icon, { className: `h-4 w-4 sm:h-5 sm:w-5 mr-2 ${getStatusDisplay(vehicle.status).color}` })}
                <span className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusDisplay(vehicle.status).bg} ${getStatusDisplay(vehicle.status).color}`}>
                  {getStatusDisplay(vehicle.status).label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 -mx-4 sm:mx-0 px-4 sm:px-0">
        <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
          {(visibleTabs.length ? visibleTabs : tabs).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap min-w-0 ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
                <span className="xs:hidden sm:hidden">
                  {tab.label.split(' ')[0]}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>


      {/* Inspection Type Selection Modal */}
      <InspectionTypeModal
        isOpen={showInspectionTypeModal}
        onClose={() => setShowInspectionTypeModal(false)}
        onTypeSelect={handleInspectionTypeSelect}
        vehicle={vehicle}
      />

      {/* Schedule Inspection Modal */}
      <ScheduleInspectionModal
        isOpen={showScheduleInspectionModal}
        onClose={() => setShowScheduleInspectionModal(false)}
        onSubmit={handleScheduleInspectionSubmit}
        vehicle={vehicle}
      />

      {/* Inspection Modal */}
      <InspectionModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        onComplete={handleInspectionComplete}
        vehicle={vehicle}
        inspectionType={inspectionType}
      />

      {/* Inspection Detail Modal */}
      <InspectionDetailModal
        isOpen={showInspectionDetailModal}
        onClose={handleCloseInspectionDetails}
        inspection={selectedInspection}
        isLoading={inspectionDetailLoading}
        error={inspectionDetailError}
        onDeleteInspection={requestDeleteInspection}
      />

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Delete Inspection</h3>
              <p className="mt-1 text-sm text-gray-600">
                Are you sure you want to permanently delete inspection {pendingDelete.inspectionNumber || pendingDelete.id}? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 space-y-2 text-sm text-gray-600">
              <p><span className="font-medium">Type:</span> {pendingDelete.type}</p>
              <p><span className="font-medium">Date:</span> {formatDate(pendingDelete.date)}</p>
              <p><span className="font-medium">Inspector:</span> {pendingDelete.inspector}</p>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={cancelDeleteInspection}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={inspectionDeleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteInspection}
                disabled={inspectionDeleteLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {inspectionDeleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleProfilePage;
